import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { ReactComponent as Close } from "../../../assets/images/teamclose.svg";
import { ReactComponent as Albert } from "../../../assets/images/ChatImages/albert.svg";
import { ReactComponent as Name } from "../../../assets/images/ChatImages/csname.svg";
import { ReactComponent as Email } from "../../../assets/images/ChatImages/csemail.svg";
import { ReactComponent as Time } from "../../../assets/images/ChatImages/cstime.svg";
import { ReactComponent as Customer } from "../../../assets/images/ChatImages/cscustomer.svg";
import { ReactComponent as Seen } from "../../../assets/images/ChatImages/cslastseen.svg";
import { ReactComponent as PageVisit } from "../../../assets/images/pagevisit.svg";
import { ReactComponent as IPAddress } from "../../../assets/images/ChatImages/ipaddress.svg";
import { ReactComponent as OS } from "../../../assets/images/ChatImages/os-device.svg";
import { ReactComponent as Browser } from "../../../assets/images/ChatImages/browser.svg";

import { ReactComponent as Location } from "../../../assets/images/ChatImages/cslocation.svg";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  getTrustchatGoogleMapsLoaderOptions,
  useGoogleMapsAuthFailed,
} from "../../../utils/googleMapsLoader";
import "./style.css";
import { Skeleton } from "@mui/material";
import { chracterPosition, msgTimeWithSeconds } from "../../../utils/index";
import { colorCodesForAvatar } from "../../../constants/index";
import { Avatar } from "@mui/material";
import {
  selectIsUserDetailSidebarOpen,
  closeUserDetailSidebar,
} from "../../../store/features/userDetailSidebarSlice";

const mapPlaceholderSx = {
  height: 256,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  textAlign: "center",
  color: "#405261",
  fontSize: 14,
  background: "#f4f6f8",
  boxSizing: "border-box",
};

function ChatSideBarGoogleMap({ apiKey, loading, center }) {
  const { isLoaded, loadError } = useJsApiLoader(
    getTrustchatGoogleMapsLoaderOptions(apiKey)
  );
  const authFailed = useGoogleMapsAuthFailed();
  const [mapInstance, setMapInstance] = useState(null);
  const containerStyle = { width: "100%", height: "256px" };

  const onLoad = useCallback((map) => setMapInstance(map), []);
  const onUnmount = useCallback(() => setMapInstance(null), []);

  useEffect(() => {
    if (!mapInstance || !center?.lat || !center?.lng) return;
    try {
      mapInstance.panTo(center);
    } catch (_) {
      /* ignore pan errors while the script reloads */
    }
  }, [mapInstance, center.lat, center.lng]);

  if (loadError || authFailed) {
    return (
      <div style={mapPlaceholderSx}>
        <p style={{ margin: 0, fontWeight: 500 }}>Could not load map</p>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7c8a" }}>
          Check the API key, billing, and HTTP referrer restrictions in Google Cloud.
        </p>
      </div>
    );
  }

  // The map instance is reused across customers and simply panned; recreating
  // it per selection made Google re-run its auth check and flash a dialog.
  return (
    <div
      className="ChatSideBar-mapClip"
      style={{
        width: "100%",
        height: 256,
        overflow: "hidden",
        position: "relative",
        borderRadius: 8,
      }}
    >
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{ fullscreenControl: false }}
        >
          <Marker position={center} />
        </GoogleMap>
      )}
      {(loading || !isLoaded) && (
        <Skeleton
          variant="rectangular"
          height={256}
          width="100%"
          animation="wave"
          sx={{ position: "absolute", inset: 0 }}
        />
      )}
    </div>
  );
}

function ChatSideBar(props) {
  const {
    getRoomId,
    customerData,
    setCustomerData,
    loading = false,
    customerId,
  } = props;
  const dispatch = useDispatch();

  const isCustomerLoading = loading || (!customerData?.length && !!customerId);

  const isUserDetailSidebarOpen = useSelector(selectIsUserDetailSidebarOpen);
  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
  const mapKeyPresent = apiKey.length > 0;

  const center = {
    lat: customerData[0]?.lat || 31.5204,
    lng: customerData[0]?.lng || 74.3587,
  };

  if (!isUserDetailSidebarOpen) return null;



  return (
    <div className="ChatSideBar-Main-Container" >
      <div className="Chat-SideBar-Heading">
        <p className="SideBar-details">Details</p>
        {/* <button
          className="UserDetail-closeBtn"
          style={{
            position: "absolute",
            right: "24px",
            top: "14px",
          }}
          onClick={() => dispatch(closeUserDetailSidebar())}
        >
          <Cross /> Close Details
        </button> */}
      </div>

      {!mapKeyPresent ? (
        <div style={mapPlaceholderSx}>
          <p style={{ margin: 0, fontWeight: 500 }}>Map unavailable</p>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7c8a" }}>
            Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in your env to show the map.
          </p>
        </div>
      ) : (
        <ChatSideBarGoogleMap
          apiKey={apiKey}
          loading={loading}
          center={center}
        />
      )}

      <div className="UserDetail-Content">
        <div className="UserDetail-Profile">
          <div className="UserDetail-profileRow">
            {loading || isCustomerLoading ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  animation="wave"
                />
                <div>
                  <Skeleton
                    variant="text"
                    width={120}
                    height={20}
                    animation="wave"
                  />
                  <Skeleton
                    variant="text"
                    width={150}
                    height={16}
                    animation="wave"
                  />
                </div>
              </div>
            ) : (
              <>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    mr: 1,
                    fontSize: "24px",
                    fontWeight: "500",
                    bgcolor:
                      colorCodesForAvatar[
                      chracterPosition(
                        (customerData[0]?.name
                          ? customerData[0]?.name.substring(0, 1)
                          : "V"
                        ).toUpperCase()
                      )
                      ],
                  }}
                >
                  {(customerData[0]?.name
                    ? customerData[0]?.name.charAt(0)
                    : "V"
                  ).toUpperCase()}
                </Avatar>
                <div style={{ width: "100%" }}>
                  <p
                    style={{ textTransform: "capitalize" }}
                    className="UserDetail-name"
                  >
                    {customerData[0]?.name?.includes('Vistor') ? customerData[0]?.ip_address : customerData[0]?.name}
                  </p>
                  <p
                    style={{
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      textTransform: "lowercase",
                      maxWidth: "280px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                    className="UserDetail-email"
                  >
                    {customerData[0]?.email}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <p className="UserDetail-divider" />
        <div className="UserDetail-Details">
          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Name />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Name"
                )}
              </p>
            </div>
            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={120}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0]?.name ? customerData[0]?.name : "( no answer )"}
              </p>
            )}
          </div>

          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Email />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Email"
                )}
              </p>
            </div>
            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={180}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue" style={{
                textTransform: "lowercase",
                maxWidth: '175px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}>
                {customerData[0]?.email ? customerData[0]?.email : "( no answer )"}
              </p>
            )}
          </div>

          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Time />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Time"
                )}
              </p>
            </div>
            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={100}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">{msgTimeWithSeconds(customerData[0]?.time)}</p>
            )}
          </div>

          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Location />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Location"
                )}
              </p>
            </div>
            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={140}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0]?.location}
              </p>
            )}
          </div>

          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Customer />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Returning Customer"
                )}
              </p>
            </div>

            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={100}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0] ? (
                  (() => {
                    const visits = customerData[0].visit_count ?? null;
                    const chats = customerData[0].chat_count ?? null;
                    const tickets = customerData[0].ticket_count ?? null;
                    const chatAndTicketStyle = {
                      color: "#061522",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "18px",
                      textDecorationLine: "underline",
                      textDecorationStyle: "solid",
                      textDecorationSkipInk: "none",
                      textDecorationThickness: "auto",
                      textUnderlineOffset: "auto",
                      textUnderlinePosition: "from-font",
                    };
                    if (visits !== null && chats !== null && tickets !== null) {
                      return (
                        <>
                          {visits} visits,{" "}
                          <span style={chatAndTicketStyle}>
                            {chats} chats
                          </span>{" "}

                          <span style={chatAndTicketStyle}>
                            {tickets} tickets
                          </span>
                        </>
                      );
                    }
                    return <span>0 visits</span>;
                  })()
                ) : (
                  <span>0 visits</span>
                )}
              </p>
            )}
          </div>

          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Seen />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Last Seen"
                )}
              </p>
            </div>
            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={150}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0]?.last_seen
                  ? (() => {
                    const lastSeenDate = new Date(customerData[0].last_seen);
                    const now = new Date();
                    const lastSeenDay = new Date(lastSeenDate.getFullYear(), lastSeenDate.getMonth(), lastSeenDate.getDate());
                    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const diffTime = nowDay - lastSeenDay;
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                    if (diffDays === 0) return "Today";
                    if (diffDays === 1) return "Yesterday";
                    if (diffDays > 1) return `${diffDays} days ago`;
                    return customerData[0].last_seen;
                  })()
                  : "( no answer )"}
              </p>
            )}

          </div>

          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <IPAddress />
              )}
              <p className="UserDetail-detailLabelText">
                {loading || isCustomerLoading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "IP Address"
                )}
              </p>
            </div>
            {loading || isCustomerLoading ? (
              <Skeleton
                variant="text"
                width={150}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0]?.ip_address
                  ? customerData[0]?.ip_address
                  : "( no answer )"}
              </p>
            )}

          </div>
          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading || isCustomerLoading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <OS />
              )}
              <p className="UserDetail-detailLabelText">
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "OS/Device"
                )}
              </p>
            </div>
            {loading ? (
              <Skeleton
                variant="text"
                width={150}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0]?.os
                  ? customerData[0]?.os
                  : "( no answer )"}
              </p>
            )}

          </div>
          <div
            style={{ paddingBottom: "19px" }}
            className="UserDetail-DetailRow"
          >
            <div className="UserDetail-detailLabel">
              {loading ? (
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  animation="wave"
                />
              ) : (
                <Browser />
              )}
              <p className="UserDetail-detailLabelText">
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={80}
                    height={20}
                    animation="wave"
                  />
                ) : (
                  "Browser"
                )}
              </p>
            </div>
            {loading ? (
              <Skeleton
                variant="text"
                width={150}
                height={24}
                animation="wave"
                style={{ marginLeft: "30px" }}
              />
            ) : (
              <p className="UserDetail-detailValue">
                {customerData[0]?.browser
                  ? customerData[0]?.browser
                  : "( no answer )"}
              </p>
            )}

          </div>
        </div>
        <div className="UserDetail-Footer">
          <div className="UserDetail-FooterHeading">
            <p className="UserDetail-visitedTitle">Visited Pages</p>
            <p className="UserDetail-visitedSummary">
              Viewed {Array.isArray(customerData[0]?.visited_pages) ? customerData[0].visited_pages.length : 0} pages
            </p>
          </div>

          {Array.isArray(customerData[0]?.visited_pages) && customerData[0].visited_pages.length > 0 ? (
            customerData[0].visited_pages.map((p) => {
              let title = p.url;
              let subtitle = p.url;
              try {
                const u = new URL(p.url);
                title = u.pathname && u.pathname !== "/" ? u.pathname : u.href;
                subtitle = u.origin + (u.pathname || "");
              } catch (e) {
                title = p.url;
                subtitle = p.referrer || p.url;
              }
              return (
                <div className="UserDetail-visitedItem" key={p._id}>
                  <p>
                    <PageVisit />
                  </p>
                  <div>
                    <p className="UserDetail-visitedItemTitle">{title}</p>
                    <p className="UserDetail-visitedItemUrl">{subtitle}</p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="UserDetail-visitedItem">
              <p>
                <PageVisit />
              </p>
              <div>
                <p className="UserDetail-visitedItemTitle">No pages</p>
                <p className="UserDetail-visitedItemUrl">—</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ChatSideBar.propTypes = {
  getRoomId: PropTypes.array.isRequired,
  customerData: PropTypes.array.isRequired,
  setCustomerData: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

ChatSideBar.defaultProps = {
  loading: false,
};

export default ChatSideBar;
